import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";

export default function AdminProfile() {
  const { id } = useParams(); // Subordinate profile dynamic routing mapping
  const navigate = useNavigate();
  const loggedUser = JSON.parse(localStorage.getItem("mediit_admin_auth") || "{}");
  const isSuperAdmin = loggedUser.role === "Super Admin";

  const [formData, setFormData] = useState({
    id: null, name: "", email: "", phone: "", role: "", password: "",
    address: "", salary: "", aadhaar: "", pan: "", photo: "", active: true
  });
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState("");

  const loadProfile = async () => {
    try {
      let url = "https://api.mediitacademy.com/api/admin/auth/ad-profile";
      let headers: any = { "Authorization": `Bearer ${loggedUser.token}` };
      
      // Agar explicit id pass ki hai subordinates track se
      if (id) {
        url = `https://api.mediitacademy.com/api/admin/auth/user/${id}`;
        headers = {};
      }

      const res = await fetch(url, { headers });
      const data = await res.json();
      setFormData({ ...data, password: "" }); // security clearance
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadProfile(); }, [id]);

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({ ...formData, photo: reader.result as string });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSuccess("");
    const targetId = id || formData.id;

    const res = await fetch(`https://api.mediitacademy.com/api/admin/auth/update-profile/${targetId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(formData)
    });

    if (res.ok) {
      setSuccess("Profile details system record me successfully register ho gyi hain!");
      if (!id) {
        // Agar khud ka data update kiya to local storage sync krein name backup k liye
        localStorage.setItem("mediit_admin_auth", JSON.stringify({ ...loggedUser, name: formData.name }));
      }
      setTimeout(() => navigate("/learn-with-us/subordinates"), 1500);
    }
  };

  if (loading) return <div className="p-8 font-black text-center text-slate-500">Processing Cloud Secure Profile Data...</div>;

  // Editable tab validation checks rule base deployment
  const canEdit = isSuperAdmin && (id !== undefined || formData.role !== "Super Admin");

  return (
    <div className="mx-auto max-w-4xl font-['Inter']">
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-black text-slate-950">Identity Profile System</h1>
          <p className="text-sm font-bold text-slate-500 mt-1">
            {canEdit ? "Manage and fulfill employee data profiles easily." : "Your company registered file dashboard profile (Read Only Matrix)."}
          </p>
        </div>
      </div>

      {success && <div className="p-4 mb-6 rounded-2xl bg-emerald-50 text-emerald-700 text-sm font-black border border-emerald-100">{success}</div>}

      <form onSubmit={handleSave} className="space-y-6">
        <div className="overflow-hidden rounded-[34px] border border-slate-200 bg-white shadow-sm">
          <div className="h-32 bg-gradient-to-r from-slate-950 to-blue-950 p-6 flex justify-end items-start" />
          
          <div className="px-8 pb-8 -mt-12">
            <div className="flex flex-col sm:flex-row sm:items-end gap-5 mb-8">
              <div className="relative group flex h-28 w-28 shrink-0 items-center justify-center rounded-3xl border-4 border-white bg-slate-100 shadow-md overflow-hidden">
                {formData.photo ? (
                  <img src={formData.photo} alt="Profile" className="h-full w-full object-cover" />
                ) : (
                  <span className="text-4xl font-black text-slate-400">{formData.name.charAt(0).toUpperCase()}</span>
                )}
                {canEdit && (
                  <label className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 cursor-pointer transition text-white text-xs font-black">
                    Upload
                    <input type="file" accept="image/*" className="hidden" onChange={handlePhotoChange} />
                  </label>
                )}
              </div>
              <div className="pb-1">
                <h2 className="text-2xl font-black text-slate-950">{formData.name || "System User"}</h2>
                <p className="text-sm font-bold text-slate-400 mt-1 flex items-center gap-2">
                  <span className="bg-slate-100 text-slate-700 px-2.5 py-1 rounded-md text-xs font-black uppercase tracking-wider">{formData.role}</span>
                </p>
              </div>
            </div>

            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label className="block text-xs font-black uppercase tracking-wider text-slate-400 mb-2">Full Name</label>
                <input disabled={!canEdit} value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3.5 text-sm font-bold text-slate-800 outline-none focus:bg-white disabled:opacity-75" />
              </div>
              <div>
                <label className="block text-xs font-black uppercase tracking-wider text-slate-400 mb-2">Email Address</label>
                <input disabled value={formData.email} className="w-full rounded-2xl border border-slate-200 bg-slate-200/60 px-4 py-3.5 text-sm font-bold text-slate-500 outline-none" />
              </div>
              <div>
                <label className="block text-xs font-black uppercase tracking-wider text-slate-400 mb-2">Phone Mobile</label>
                <input disabled={!canEdit} value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })} className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3.5 text-sm font-bold text-slate-800 outline-none focus:bg-white disabled:opacity-75" />
              </div>
              <div>
                <label className="block text-xs font-black uppercase tracking-wider text-slate-400 mb-2">Access Portal Password {canEdit && "(Optional Update)"}</label>
                <input type="password" disabled={!canEdit} placeholder={canEdit ? "Enter new custom password" : "••••••••"} value={formData.password} onChange={e => setFormData({ ...formData, password: e.target.value })} className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3.5 text-sm font-bold text-slate-800 outline-none focus:bg-white disabled:opacity-75" />
              </div>
            </div>

            <div className="mt-6 border-t border-slate-100 pt-6">
              <h3 className="text-sm font-black uppercase tracking-wider text-blue-600 mb-4">Complete Payroll & Document Matrix</h3>
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label className="block text-xs font-black uppercase tracking-wider text-slate-400 mb-2">Assigned Monthly Salary (INR)</label>
                  <input type="number" disabled={!canEdit} placeholder="Not Assigned" value={formData.salary || ""} onChange={e => setFormData({ ...formData, salary: e.target.value })} className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3.5 text-sm font-bold text-slate-800 outline-none focus:bg-white disabled:opacity-75" />
                </div>
                <div>
                  <label className="block text-xs font-black uppercase tracking-wider text-slate-400 mb-2">Aadhaar Card Identification (Optional)</label>
                  <input disabled={!canEdit} placeholder="Not Provided" value={formData.aadhaar || ""} onChange={e => setFormData({ ...formData, aadhaar: e.target.value })} className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3.5 text-sm font-bold text-slate-800 outline-none focus:bg-white disabled:opacity-75" />
                </div>
                <div>
                  <label className="block text-xs font-black uppercase tracking-wider text-slate-400 mb-2">Permanent Account Number (PAN) (Optional)</label>
                  <input disabled={!canEdit} placeholder="Not Provided" value={formData.pan || ""} onChange={e => setFormData({ ...formData, pan: e.target.value })} className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3.5 text-sm font-bold text-slate-800 outline-none focus:bg-white disabled:opacity-75" />
                </div>
                <div>
                  <label className="block text-xs font-black uppercase tracking-wider text-slate-400 mb-2">Residential Communication Address</label>
                  <input disabled={!canEdit} placeholder="Not Filled" value={formData.address || ""} onChange={e => setFormData({ ...formData, address: e.target.value })} className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3.5 text-sm font-bold text-slate-800 outline-none focus:bg-white disabled:opacity-75" />
                </div>
              </div>
            </div>

            {canEdit && (
              <div className="mt-8 flex justify-end gap-3 border-t border-slate-100 pt-6">
                <button type="button" onClick={() => navigate("/learn-with-us/subordinates")} className="rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-black text-slate-700 hover:bg-slate-50">Cancel</button>
                <button type="submit" className="rounded-2xl bg-blue-600 px-6 py-3 text-sm font-black text-white shadow-lg shadow-blue-500/20 hover:bg-blue-700">Save System Record</button>
              </div>
            )}
          </div>
        </div>
      </form>
    </div>
  );
}